import { useState } from 'react';
import { Download, X, Wifi, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePWA } from '@/hooks/use-pwa';
import { cn } from '@/lib/utils';

export function PWAInstallBanner() {
  const { canInstall, isOnline, promptInstall } = usePWA();
  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem('pwa-banner-dismissed') === 'true';
  });

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('pwa-banner-dismissed', 'true');
  };

  const handleInstall = async () => {
    const installed = await promptInstall();
    if (installed) {
      handleDismiss();
    }
  };

  // Show offline indicator
  if (!isOnline) {
    return (
      <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-50">
        <div className="bg-yellow-500/90 backdrop-blur-sm text-yellow-900 rounded-xl p-4 shadow-lg flex items-center gap-3">
          <WifiOff className="h-5 w-5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-sm">You're offline</p>
            <p className="text-xs opacity-80">Some features may be limited</p>
          </div>
        </div>
      </div>
    );
  }

  // Don't show install banner if dismissed or can't install
  if (dismissed || !canInstall) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50 animate-in slide-in-from-bottom-4">
      <div className="bg-slate-800/95 backdrop-blur-sm border border-slate-700 rounded-xl p-4 shadow-xl">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <Download className="h-5 w-5 text-green-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-white text-sm">Install FraudCheck BD</h3>
            <p className="text-xs text-slate-400 mt-1">
              Add to your home screen for quick access and offline support
            </p>
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                onClick={handleInstall}
                className="bg-green-600 hover:bg-green-700 text-xs h-8"
              >
                Install App
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDismiss}
                className="text-slate-400 hover:text-white text-xs h-8"
              >
                Not now
              </Button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-slate-500 hover:text-white transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function OfflineIndicator() {
  const { isOnline } = usePWA();

  return (
    <div
      className={cn(
        "fixed top-20 left-1/2 -translate-x-1/2 z-50 transition-all duration-300",
        isOnline ? "opacity-0 -translate-y-4 pointer-events-none" : "opacity-100 translate-y-0"
      )}
    >
      <div className="bg-yellow-500 text-yellow-900 rounded-full px-4 py-2 shadow-lg flex items-center gap-2 text-sm font-medium">
        <WifiOff className="h-4 w-4" />
        Offline Mode
      </div>
    </div>
  );
}
